/**
 * About page — /about
 * 8-section structure: Thesis → Market problem → Founding logic →
 * Team → Full-Stack Method → How we work → Who we work with → Locations → CTA
 * + Full-Stack Search Method module + Client logos marquee
 * Dark Tech design: #0c0c0c bg, #57d7e2 cyan, Barlow font
 */

import { useState } from "react";
import SiteLayout from "@/components/SiteLayout";
import MobileSlider from "@/components/MobileSlider";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useIsMobile } from "@/hooks/useIsMobile";
import { VIDEO_REVIEW_URL } from "@/lib/cta";
import { TEAM } from "@/lib/team";

const ff = "'Barlow', sans-serif";
const cyan = "#57d7e2";

const CDN = "https://d2xsxph8kpxj0f.cloudfront.net/310519663444343231/KfbWTnTA9XstaA7FAytXMj";
const HERO_IMG = `${CDN}/about-hero-signal_378981aa.png`;

// ── Client logos (same as homepage) ───────────────────────────────────
const LOGO_ASSETS = [
  `/4941-9030.webp`, `/4941-9031.webp`,
  `/4941-9032.webp`, `/4941-9033.webp`,
  `/4941-9034.webp`, `/4941-9035.webp`,
  `/4941-9036.webp`, `/4941-9037.webp`,
  `/4941-9038.webp`, `/4941-9039.webp`,
  `/4941-9040.webp`, `/4941-9041.webp`,
  `/4941-9042.webp`, `/4941-9043.webp`,
  `/4941-9044.webp`, `/4941-9045.webp`,
  `/4941-9046.webp`, `/4941-9047.webp`,
  `/4941-9048.webp`, `/4941-9049.webp`,
  `/4941-9050.webp`, `/4941-9051.webp`,
  `/4941-9052.webp`, `/4941-9053.webp`,
  `${CDN}/smartfog_87dd78bd.webp`,
];

// ── Principles ─────────────────────────────────────────────────────────
const PRINCIPLES = [
  {
    title: "Commercial framing before strategy",
    desc: "We start with your revenue model, sales cycle, and ICP — not a keyword list. Every recommendation is filtered through what actually moves pipeline.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <path d="M14 3v22M3 14h22" stroke={cyan} strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
        <circle cx="14" cy="14" r="6" stroke={cyan} strokeWidth="1.6" opacity="0.8" />
        <circle cx="14" cy="14" r="2.5" fill={cyan} opacity="0.6" />
      </svg>
    ),
  },
  {
    title: "Measurable outcomes only",
    desc: "We don't report on rankings or traffic in isolation. Every engagement is tracked against pipeline generated and revenue closed. If we can't measure it, we don't do it.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <path d="M4 22l6-8 5 4 5-9 4 5" stroke={cyan} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" opacity="0.8" />
        <circle cx="10" cy="14" r="2" fill={cyan} opacity="0.3" />
        <circle cx="20" cy="9" r="2" fill={cyan} opacity="0.3" />
      </svg>
    ),
  },
  {
    title: "Selective client base",
    desc: "We take on 3–4 new clients per month. This isn't artificial scarcity — it's the only way to keep senior practitioners on every account instead of handing work to juniors after the pitch.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <path d="M14 4l2.5 5.5H22l-4.5 3.5L19 18.5 14 15l-5 3.5 1.5-5.5L6 9.5h5.5z" stroke={cyan} strokeWidth="1.5" strokeLinejoin="round" opacity="0.8" />
      </svg>
    ),
  },
  {
    title: "Full-stack delivery, not task lists",
    desc: "Strategy, content, technical, links, and tracking operate as one system. We don't hand you a document and walk away — we implement, measure, and iterate.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <rect x="4" y="4" width="20" height="20" rx="3" stroke={cyan} strokeWidth="1.5" opacity="0.4" />
        <path d="M10 14l3 3 5-6" stroke={cyan} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.8" />
      </svg>
    ),
  },
];

// ── Market problem statements ──────────────────────────────────────────
const MARKET_PROBLEMS = [
  {
    heading: "Activity over outcomes",
    text: "Most agencies sell SEO as a set of tasks — audits, content calendars, link reports — and measure success in rankings and traffic. The problem is that none of those metrics tell you whether search is actually generating revenue.",
  },
  {
    heading: "Vanity metrics, flat pipeline",
    text: "The result is a market full of agencies that can show you a keyword graph going up while your pipeline stays flat. They optimise for activity because attribution is harder, so most don't attempt it.",
  },
  {
    heading: "The unanswered question",
    text: "Meanwhile, the companies paying for this work can't answer a basic question: is our SEO investment generating a return? If your agency can't answer that question clearly, the engagement is already broken.",
  },
];

// ── Placeholder avatar component ───────────────────────────────────────
// Placeholder for team members without a headshot yet — mirrors the branded
// teal backdrop of the real photos (faint INDEXED wordmark) with a faceless
// shadow silhouette where the person would be.
function PlaceholderAvatar() {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        overflow: "hidden",
        background: "linear-gradient(150deg, #16302f 0%, #0e2221 55%, #0a1918 100%)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      {/* faint INDEXED wordmark, same brand cue as the photo backdrops */}
      <span
        style={{
          position: "absolute",
          top: "16%",
          left: "50%",
          transform: "translateX(-50%)",
          fontFamily: ff,
          fontWeight: 800,
          fontSize: "30px",
          letterSpacing: "0.18em",
          color: "rgba(87,215,226,0.07)",
          whiteSpace: "nowrap",
          userSelect: "none",
        }}
      >
        INDEXED
      </span>
      {/* faceless shadow silhouette (head + shoulders) rising from the bottom */}
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMax meet"
        style={{ width: "78%", height: "78%", display: "block" }}
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="silh" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(87,215,226,0.16)" />
            <stop offset="100%" stopColor="rgba(10,25,24,0.45)" />
          </linearGradient>
        </defs>
        <circle cx="50" cy="34" r="19" fill="url(#silh)" />
        <path d="M9 100 C9 73 27 60 50 60 C73 60 91 73 91 100 Z" fill="url(#silh)" />
      </svg>
    </div>
  );
}

// ── Team card with hover ───────────────────────────────────────────────
// `featured` gives the lead (Anjan) a wider, accented treatment: on desktop the
// card spans 2 grid columns with a horizontal photo-left / bio-right layout.
function TeamCard({ member, isMobile, featured = false }: { member: (typeof TEAM)[0]; isMobile: boolean; featured?: boolean }) {
  const [hov, setHov] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const href = (member as { href?: string }).href;
  const Wrapper = href ? "a" : "div";
  // Wide horizontal layout only makes sense with the extra width we get on desktop.
  const wide = featured && !isMobile;

  return (
    <Wrapper
      {...(href ? { href } : {})}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: wide ? "flex" : "block",
        flexDirection: wide ? "row" : undefined,
        gridColumn: wide ? "span 2" : undefined,
        textDecoration: "none",
        cursor: href ? "pointer" : "default",
        // Featured card carries a persistent cyan accent so it stands out even at rest.
        background: featured
          ? hov ? "rgba(87,215,226,0.07)" : "rgba(87,215,226,0.04)"
          : hov ? "rgba(87,215,226,0.03)" : "rgba(255,255,255,0.02)",
        border: `1px solid ${featured ? (hov ? "rgba(87,215,226,0.4)" : "rgba(87,215,226,0.25)") : hov ? "rgba(87,215,226,0.2)" : "rgba(255,255,255,0.06)"}`,
        borderRadius: "16px",
        overflow: "hidden",
        transition: "all 0.25s ease",
        transform: hov ? "translateY(-4px)" : "translateY(0)",
      }}
    >
      <div style={{ position: "relative", aspectRatio: wide ? undefined : "1/1", width: wide ? "44%" : undefined, flexShrink: wide ? 0 : undefined, overflow: "hidden" }}>
        {member.img && !imgFailed ? (
          <img
            loading="lazy"
            src={member.img}
            alt={member.name}
            onError={() => setImgFailed(true)}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "top",
              display: "block",
              transition: "transform 0.4s ease",
              transform: hov ? "scale(1.05)" : "scale(1)",
            }}
          />
        ) : (
          <PlaceholderAvatar />
        )}
        {featured && (
          <span
            style={{
              position: "absolute",
              top: "12px",
              left: "12px",
              background: cyan,
              color: "#0a0a0a",
              fontSize: "10px",
              fontWeight: 800,
              fontFamily: ff,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              padding: "4px 10px",
              borderRadius: "88px",
            }}
          >
            Founder
          </span>
        )}
      </div>
      <div style={{ padding: wide ? "32px" : isMobile ? "14px 12px" : "20px", display: wide ? "flex" : undefined, flexDirection: wide ? "column" : undefined, justifyContent: wide ? "center" : undefined }}>
        <div style={{ color: "white", fontSize: wide ? "26px" : isMobile ? "15px" : "17px", fontWeight: 700, fontFamily: ff, marginBottom: "3px", letterSpacing: wide ? "-0.01em" : undefined }}>
          {member.name}
        </div>
        <div style={{ color: cyan, fontSize: wide ? "14px" : isMobile ? "12px" : "13px", fontWeight: 600, fontFamily: ff, marginBottom: wide ? "14px" : isMobile ? "8px" : "10px" }}>
          {member.role}
        </div>
        <p
          style={{
            color: "rgba(255,255,255,0.45)",
            fontSize: wide ? "15px" : isMobile ? "12px" : "14px",
            fontFamily: ff,
            lineHeight: wide ? 1.7 : 1.6,
            margin: 0,
            display: isMobile && !wide ? "-webkit-box" : undefined,
            WebkitLineClamp: isMobile && !wide ? 3 : undefined,
            WebkitBoxOrient: isMobile && !wide ? ("vertical" as const) : undefined,
            overflow: isMobile && !wide ? "hidden" : undefined,
          }}
        >
          {member.bio}
        </p>
        {href && (
          <span style={{ color: cyan, fontSize: wide ? "14px" : "13px", fontWeight: 600, fontFamily: ff, marginTop: wide ? "18px" : "12px", display: "inline-flex", alignItems: "center", gap: "6px" }}>
            View profile <span style={{ transition: "transform 0.2s ease", transform: hov ? "translateX(3px)" : "translateX(0)" }}>→</span>
          </span>
        )}
      </div>
    </Wrapper>
  );
}

// ── Principle card with hover ──────────────────────────────────────────
function PrincipleCard({ p, i, isMobile }: { p: (typeof PRINCIPLES)[0]; i: number; isMobile: boolean }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: "relative",
        background: hov ? "rgba(87,215,226,0.04)" : "rgba(255,255,255,0.02)",
        border: `1px solid ${hov ? "rgba(87,215,226,0.2)" : "rgba(255,255,255,0.07)"}`,
        borderRadius: "20px",
        padding: isMobile ? "28px 24px" : "40px 36px",
        transition: "all 0.25s ease",
        transform: hov ? "translateY(-3px)" : "translateY(0)",
        overflow: "hidden",
      }}
    >
      <div style={{ position: "absolute", top: "-12px", right: "16px", fontSize: "120px", fontWeight: 900, fontFamily: ff, color: "rgba(87,215,226,0.04)", lineHeight: 1, userSelect: "none", letterSpacing: "-0.04em" }}>
        0{i + 1}
      </div>
      <div style={{ width: "52px", height: "52px", borderRadius: "14px", background: "rgba(87,215,226,0.08)", border: "1px solid rgba(87,215,226,0.12)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "20px" }}>
        {p.icon}
      </div>
      <div style={{ color: "white", fontSize: isMobile ? "18px" : "20px", fontWeight: 700, fontFamily: ff, marginBottom: "12px", lineHeight: 1.25, position: "relative" }}>
        {p.title}
      </div>
      <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "15px", fontFamily: ff, lineHeight: 1.7, margin: 0, position: "relative" }}>
        {p.desc}
      </p>
    </div>
  );
}

export default function About() {
  useSeoMeta({
    title: "About Indexed | The SEO Agency Built for Revenue",
    description: "Indexed is a full-stack SEO agency founded on a simple thesis: search should be measured in revenue, not rankings. Meet the team behind the method.",
    canonical: "/about",
    ogType: "website",
    jsonLd: [
      {
        "@type": "Organization",
        "@id": "https://www.joinindexed.com/#organization",
        name: "Indexed",
        url: "https://www.joinindexed.com",
        description: "Indexed is a full-stack SEO agency that measures success in pipeline and revenue, not rankings and traffic.",
        foundingDate: "2024",
        numberOfEmployees: { "@type": "QuantitativeValue", value: 14 },
        address: {
          "@type": "PostalAddress",
          streetAddress: "Meydan Grandstand, Meydan Road, Nad Al Sheba",
          addressLocality: "Dubai",
          addressCountry: "AE",
        },
        sameAs: [
          "https://share.google/AvRKwCYP0CI2KeICu",
          "https://www.linkedin.com/company/indexedseo",
          "https://x.com/Indexed_Agency",
          "https://www.instagram.com/indexedagency",
          "https://www.facebook.com/indexedseoagency",
        ],
      },
    ],
  });

  const isMobile = useIsMobile();

  return (
    <SiteLayout>
      {/* ═══════════════════════════════════════════════════════════════
          1. OPENING THESIS — hero with background image
      ═══════════════════════════════════════════════════════════════ */}
      <section
        style={{
          position: "relative",
          minHeight: isMobile ? "70vh" : "80vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {/* Background image */}
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(115% 125% at 78% -10%, #0f3036 0%, #0b1719 38%, #070b0b 72%)", backgroundSize: "cover", backgroundPosition: "center 30%", zIndex: 0 }} />
        {/* Cyan glow */}
        <div style={{ position: "absolute", width: "400px", height: "600px", left: "-100px", top: "-150px", background: "#3aefff", filter: "blur(175px)", opacity: 0.1, zIndex: 1, pointerEvents: "none" }} />
        {/* Bottom gradient */}
        <div style={{ position: "absolute", width: "100%", height: "65%", left: 0, bottom: 0, background: "linear-gradient(to bottom,rgba(0,0,0,0) 0%,rgba(0,0,0,0.55) 50%,rgba(0,0,0,0.95) 85%,rgba(0,0,0,1) 100%)", zIndex: 2, pointerEvents: "none" }} />
        {/* Left gradient for text legibility */}
        <div style={{ position: "absolute", top: 0, left: 0, width: "70%", height: "100%", background: "linear-gradient(to right,rgba(0,0,0,0.85) 0%,rgba(0,0,0,0.6) 50%,rgba(0,0,0,0) 100%)", zIndex: 3, pointerEvents: "none" }} />
        <div style={{ position: "relative", zIndex: 10, padding: isMobile ? "140px 20px 60px" : "180px 72px 100px", maxWidth: "1440px", margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
        <div style={{ maxWidth: "920px" }}>
          <h1
            style={{
              color: "white",
              fontSize: isMobile ? "34px" : "clamp(44px, 5vw, 64px)",
              fontWeight: 700,
              fontFamily: ff,
              lineHeight: 1.08,
              letterSpacing: "-0.03em",
              margin: "0 0 36px",
              textAlign: isMobile ? "center" : "left",
            }}
          >
            SEO that isn't measured in{" "}
            <span style={{ color: cyan, position: "relative" }}>
              revenue
              <span style={{ position: "absolute", bottom: "-4px", left: 0, right: 0, height: "3px", background: `linear-gradient(90deg, ${cyan}, transparent)`, borderRadius: "2px", opacity: 0.5 }} />
            </span>{" "}
            isn't SEO — it's activity.
          </h1>
          <p
            style={{
              color: "rgba(255,255,255,0.55)",
              fontSize: isMobile ? "16px" : "19px",
              fontFamily: ff,
              lineHeight: 1.8,
              margin: 0,
              maxWidth: "640px",
              textAlign: isMobile ? "center" : "left",
            }}
          >
            That's the position we built Indexed on. Not because it's contrarian, but because every other
            approach we'd seen — inside agencies, inside companies, inside portfolio businesses — failed
            to connect search activity to commercial outcomes.
          </p>
        </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          CLIENT LOGOS — scrolling marquee (same as homepage)
      ═══════════════════════════════════════════════════════════════ */}
      <section style={{ background: "#080808" }}>
        <div style={{ position: "relative", borderTop: "1px solid rgba(255,255,255,0.08)", borderBottom: "1px solid rgba(255,255,255,0.08)", padding: "16px 0", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, #080808 0%, transparent 12%, transparent 88%, #080808 100%)", zIndex: 2, pointerEvents: "none" }} />
          <div style={{ display: "flex", alignItems: "center", gap: "50px", animation: "scroll-logos 50s linear infinite", width: "max-content" }}>
            {[...LOGO_ASSETS, ...LOGO_ASSETS].map((src, idx) => (
              <img loading="lazy" key={idx} src={src} alt={`client logo ${idx}`} style={{ width: "135px", height: "90px", objectFit: "contain", flexShrink: 0, filter: "grayscale(1) brightness(2)" }} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          2. THE PROBLEM WITH THE MARKET — 3 distinct statement blocks
      ═══════════════════════════════════════════════════════════════ */}
      <section
        style={{
          background: "#0c0c0c",
          padding: isMobile ? "70px 20px" : "120px 72px",
          position: "relative",
        }}
      >
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ marginBottom: isMobile ? "48px" : "72px", maxWidth: "600px" }}>
            <h2
              style={{
                color: "white",
                fontSize: isMobile ? "28px" : "clamp(34px, 3.5vw, 46px)",
                fontWeight: 700,
                fontFamily: ff,
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                margin: 0,
              }}
            >
              What most SEO agencies{" "}
              <span style={{ color: cyan }}>get wrong.</span>
            </h2>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
              gap: isMobile ? "28px" : "32px",
            }}
          >
            {MARKET_PROBLEMS.map((item, i) => (
              <div
                key={i}
                style={{
                  borderLeft: `3px solid ${i === 0 ? cyan : "rgba(87,215,226,0.25)"}`,
                  paddingLeft: "24px",
                }}
              >
                <div style={{ color: "white", fontSize: "18px", fontWeight: 700, fontFamily: ff, marginBottom: "12px", lineHeight: 1.3 }}>
                  {item.heading}
                </div>
                <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "15px", fontFamily: ff, lineHeight: 1.75, margin: 0 }}>
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          3. WHY INDEXED EXISTS — asymmetric 2-col with pull quote
      ═══════════════════════════════════════════════════════════════ */}
      <section
        style={{
          background: "#080808",
          padding: isMobile ? "70px 20px" : "120px 72px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: "50%", right: "-100px", transform: "translateY(-50%)", width: "400px", height: "400px", background: "radial-gradient(circle, rgba(87,215,226,0.04) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: isMobile ? "40px" : "72px",
            alignItems: "center",
            position: "relative",
          }}
        >
          {/* Left: narrative */}
          <div>
            <h2
              style={{
                color: "white",
                fontSize: isMobile ? "28px" : "clamp(34px, 3.5vw, 46px)",
                fontWeight: 700,
                fontFamily: ff,
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                margin: "0 0 28px",
              }}
            >
              Why <span style={{ color: cyan }}>Indexed</span> exists.
            </h2>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                color: "rgba(255,255,255,0.55)",
                fontSize: "16px",
                fontFamily: ff,
                lineHeight: 1.8,
              }}
            >
              <p style={{ margin: 0 }}>
                Indexed was founded by Anjan after a career in corporate advisory and private equity — not
                marketing. That background matters because it shaped how the agency thinks about SEO: as a
                capital allocation problem, not a creative one.
              </p>
              <p style={{ margin: 0 }}>
                In PE, every investment is evaluated against return. The question is never "are we doing
                enough activity?" — it's "is this generating a measurable commercial outcome?" Anjan built
                Indexed to apply that same discipline to organic search.
              </p>
            </div>
          </div>

          {/* Right: pull quote + closing paragraph */}
          <div>
            <div
              style={{
                background: "rgba(87,215,226,0.04)",
                border: "1px solid rgba(87,215,226,0.12)",
                borderRadius: "20px",
                padding: isMobile ? "32px 24px" : "44px 36px",
                marginBottom: "28px",
                position: "relative",
              }}
            >
              <div style={{ position: "absolute", top: "16px", left: "24px", fontSize: "72px", fontFamily: "Georgia, serif", color: cyan, opacity: 0.15, lineHeight: 1, userSelect: "none" }}>
                &ldquo;
              </div>
              <p style={{ color: "white", fontSize: isMobile ? "18px" : "22px", fontWeight: 600, fontFamily: ff, lineHeight: 1.5, margin: 0, position: "relative", letterSpacing: "-0.01em" }}>
                An agency that starts with your revenue model, not a keyword list. One that reports on
                pipeline generated, not pages indexed.
              </p>
            </div>
            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "15px", fontFamily: ff, lineHeight: 1.75, margin: 0, paddingLeft: isMobile ? "0" : "4px" }}>
              The result is an agency that selects clients based on whether the engagement can produce a
              genuine return — not just whether the retainer clears.
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          TEAM SECTION — cards with hover effects (moved up)
      ═══════════════════════════════════════════════════════════════ */}
      <section
        style={{
          background: "#0c0c0c",
          padding: isMobile ? "70px 20px" : "120px 72px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "800px", height: "400px", background: "radial-gradient(ellipse, rgba(87,215,226,0.03) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ maxWidth: "1100px", margin: "0 auto", position: "relative" }}>
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "center" : "flex-end",
              gap: "16px",
              marginBottom: isMobile ? "48px" : "64px",
              textAlign: isMobile ? "center" : "left",
            }}
          >
            <div>
              <h2
                style={{
                  color: "white",
                  fontSize: isMobile ? "28px" : "clamp(34px, 3.5vw, 46px)",
                  fontWeight: 700,
                  fontFamily: ff,
                  lineHeight: 1.1,
                  letterSpacing: "-0.02em",
                  margin: 0,
                }}
              >
                The people behind{" "}
                <span style={{ color: cyan }}>the work.</span>
              </h2>
            </div>
            <p
              style={{
                color: "rgba(255,255,255,0.4)",
                fontSize: "15px",
                fontFamily: ff,
                lineHeight: 1.6,
                margin: 0,
                maxWidth: "340px",
              }}
            >
              Senior practitioners on every account. No hand-offs, no junior teams running your strategy.
            </p>
          </div>

          {isMobile ? (
            <MobileSlider itemWidth={200} gap={12} padding={20}>
              {TEAM.map((member, i) => (
                <TeamCard key={i} member={member} isMobile={isMobile} featured={!!(member as { href?: string }).href} />
              ))}
            </MobileSlider>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "20px",
              }}
            >
              {TEAM.map((member, i) => (
                <TeamCard key={i} member={member} isMobile={isMobile} featured={!!(member as { href?: string }).href} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          FULL-STACK SEARCH METHOD — exact same module as homepage
      ═══════════════════════════════════════════════════════════════ */}
      <section className="section-grid" style={{ backgroundColor: "#0a0c0c", padding: isMobile ? "60px 20px 40px" : "100px 72px 80px", overflow: "hidden" }}>
        <h2 style={{
          textAlign: "center", fontSize: isMobile ? "32px" : "48px", fontWeight: 700,
          fontFamily: ff, lineHeight: 1.1, margin: "0 auto 64px", letterSpacing: "-0.02em",
          maxWidth: "900px",
        }}>
          The Indexed Full-Stack<br/><span style={{ color: cyan }}>Search Method&trade;</span>
        </h2>

        <div style={{ maxWidth: "640px", margin: "0 auto", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>

          {/* Tier 1 — Revenue (top / narrowest) */}
          <div style={{
            width: isMobile ? "60%" : "42%",
            background: "linear-gradient(180deg, rgba(87,215,226,0.22) 0%, rgba(87,215,226,0.10) 100%)",
            border: "1px solid rgba(87,215,226,0.55)",
            borderRadius: "12px",
            padding: isMobile ? "18px 16px" : "22px 24px",
            textAlign: "center" as const,
            boxSizing: "border-box" as const,
          }}>
            <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "radial-gradient(circle, #7af0ff 0%, #20b8c8 60%, #0e7a88 100%)", boxShadow: "0 0 18px 6px rgba(87,215,226,0.4)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
              <span style={{ color: "white", fontSize: "14px", fontWeight: 800, fontFamily: ff }}>$</span>
            </div>
            <div style={{ color: "white", fontSize: isMobile ? "15px" : "17px", fontWeight: 700, fontFamily: ff, lineHeight: 1.2, marginBottom: "5px" }}>Revenue</div>
            <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "13px", fontFamily: ff, lineHeight: 1.5 }}>Pipeline that compounds over time</div>
          </div>

          {/* Upward arrow */}
          <div style={{ display: "flex", justifyContent: "center", height: "28px", alignItems: "center" }}>
            <svg width="20" height="28" viewBox="0 0 20 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line x1="10" y1="28" x2="10" y2="8" stroke="rgba(87,215,226,0.4)" strokeWidth="1.5" strokeDasharray="3 3"/>
              <path d="M4 10L10 2L16 10" stroke="rgba(87,215,226,0.6)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
          </div>

          {/* Tier 2 — Visibility (middle width) */}
          <div style={{
            width: isMobile ? "82%" : "68%",
            background: "linear-gradient(180deg, rgba(87,215,226,0.13) 0%, rgba(87,215,226,0.05) 100%)",
            border: "1px solid rgba(87,215,226,0.32)",
            borderRadius: "12px",
            padding: isMobile ? "18px 16px" : "22px 24px",
            textAlign: "center" as const,
            boxSizing: "border-box" as const,
          }}>
            <div style={{ color: "white", fontSize: isMobile ? "15px" : "17px", fontWeight: 700, fontFamily: ff, lineHeight: 1.2, marginBottom: "5px" }}>Visibility Across Google &amp; LLMs</div>
            <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "13px", fontFamily: ff, lineHeight: 1.5 }}>Search engines and AI models reward the same underlying signals</div>
          </div>

          {/* Upward arrow */}
          <div style={{ display: "flex", justifyContent: "center", height: "28px", alignItems: "center" }}>
            <svg width="20" height="28" viewBox="0 0 20 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line x1="10" y1="28" x2="10" y2="8" stroke="rgba(87,215,226,0.4)" strokeWidth="1.5" strokeDasharray="3 3"/>
              <path d="M4 10L10 2L16 10" stroke="rgba(87,215,226,0.6)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
          </div>

          {/* Tier 3 — Foundation / 7 Pillars (widest / bottom) */}
          <div style={{
            width: "100%",
            background: "linear-gradient(180deg, rgba(87,215,226,0.07) 0%, rgba(87,215,226,0.02) 100%)",
            border: "1px solid rgba(87,215,226,0.18)",
            borderRadius: "12px",
            padding: isMobile ? "18px 16px" : "22px 24px",
            textAlign: "center" as const,
            boxSizing: "border-box" as const,
          }}>
            <div style={{ color: "white", fontSize: isMobile ? "15px" : "17px", fontWeight: 700, fontFamily: ff, lineHeight: 1.2, marginBottom: "5px" }}>7 Integrated Pillars</div>
            <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "13px", fontFamily: ff, lineHeight: 1.8 }}>
              Growth Strategy &middot; Technical Foundations &middot; Content Engine &middot; Experience &amp; CRO &middot; Web Dev &middot; Authority Building &middot; Measurement
            </div>
          </div>

        </div>

        {/* Methodology CTA */}
        <div style={{ textAlign: "center", marginTop: "48px" }}>
          <a
            href="/method"
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
              border: "1px solid #57d7e2",
              borderRadius: "88px",
              padding: "16px 32px",
              boxShadow: "0px 2px 15px 0px rgba(87,215,226,0.20)",
              color: "#121212",
              fontSize: "16px",
              fontWeight: 600,
              fontFamily: ff,
              textDecoration: "none",
              cursor: "pointer",
              transition: "opacity 0.2s",
            }}
            onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "0.85"}
            onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "1"}
          >
            See the full methodology
          </a>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          4. HOW WE WORK — principle cards with icons + faded numbers
      ═══════════════════════════════════════════════════════════════ */}
      <section
        style={{
          background: "#0c0c0c",
          padding: isMobile ? "70px 20px" : "120px 72px",
          position: "relative",
        }}
      >
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "flex-end",
              gap: "16px",
              marginBottom: isMobile ? "48px" : "64px",
            }}
          >
            <div>
              <h2
                style={{
                  color: "white",
                  fontSize: isMobile ? "28px" : "clamp(34px, 3.5vw, 46px)",
                  fontWeight: 700,
                  fontFamily: ff,
                  lineHeight: 1.1,
                  letterSpacing: "-0.02em",
                  margin: 0,
                }}
              >
                How we <span style={{ color: cyan }}>work.</span>
              </h2>
            </div>
            <p
              style={{
                color: "rgba(255,255,255,0.4)",
                fontSize: "15px",
                fontFamily: ff,
                lineHeight: 1.6,
                margin: 0,
                maxWidth: "340px",
              }}
            >
              Four principles that guide every engagement. Each one quietly disqualifies the wrong client.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "24px",
            }}
          >
            {PRINCIPLES.map((p, i) => (
              <PrincipleCard key={i} p={p} i={i} isMobile={isMobile} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          5. WHO WE WORK WITH — contrasting panels
      ═══════════════════════════════════════════════════════════════ */}
      <section
        style={{
          background: "#080808",
          padding: isMobile ? "70px 20px" : "120px 72px",
          position: "relative",
        }}
      >
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ marginBottom: isMobile ? "48px" : "64px", textAlign: "center" }}>
            <h2
              style={{
                color: "white",
                fontSize: isMobile ? "28px" : "clamp(34px, 3.5vw, 46px)",
                fontWeight: 700,
                fontFamily: ff,
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                margin: 0,
              }}
            >
              Who we <span style={{ color: cyan }}>work with.</span>
            </h2>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "24px",
            }}
          >
            {/* We work with — positive panel */}
            <div
              style={{
                background: "rgba(87,215,226,0.03)",
                border: "1px solid rgba(87,215,226,0.12)",
                borderRadius: "20px",
                padding: isMobile ? "32px 24px" : "44px 36px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "24px" }}>
                <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: "rgba(87,215,226,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12l5 5L20 7" stroke={cyan} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span style={{ color: cyan, fontSize: "14px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                  Good fit
                </span>
              </div>
              <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "15px", fontFamily: ff, lineHeight: 1.8, display: "flex", flexDirection: "column", gap: "16px" }}>
                <p style={{ margin: 0 }}>Growth-stage SaaS companies, VC-backed operators, PE portfolio businesses, and professional services firms with genuine commercial ambition.</p>
                <p style={{ margin: 0 }}>They've usually tried SEO before — sometimes in-house, sometimes with an agency — and hit a ceiling because the work was never connected to revenue.</p>
                <p style={{ margin: 0 }}>Companies with a clear ICP, an established sales process, and leadership that treats organic as a growth channel — not a checkbox.</p>
              </div>
            </div>

            {/* We don't work with — exclusion panel */}
            <div
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: "20px",
                padding: isMobile ? "32px 24px" : "44px 36px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "24px" }}>
                <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: "rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="rgba(255,255,255,0.4)" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                </div>
                <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "14px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                  Not a fit
                </span>
              </div>
              <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "15px", fontFamily: ff, lineHeight: 1.8, display: "flex", flexDirection: "column", gap: "16px" }}>
                <p style={{ margin: 0 }}>Companies looking for the cheapest option.</p>
                <p style={{ margin: 0 }}>Businesses without a defined sales process.</p>
                <p style={{ margin: 0 }}>Teams that want to outsource strategy entirely and check in quarterly.</p>
                <p style={{ margin: 0, fontStyle: "italic", opacity: 0.7 }}>If SEO is a line item you'd rather not think about, we're not the right fit.</p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════════════
          6. OUR LOCATIONS — office addresses
      ═══════════════════════════════════════════════════════════════ */}
      <section
        style={{
          background: "#0c0c0c",
          padding: isMobile ? "70px 20px" : "120px 72px",
          position: "relative",
        }}
      >
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ marginBottom: isMobile ? "48px" : "64px", textAlign: "center" }}>
            <h2
              style={{
                color: "white",
                fontSize: isMobile ? "28px" : "clamp(34px, 3.5vw, 46px)",
                fontWeight: 700,
                fontFamily: ff,
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                margin: 0,
              }}
            >
              Our <span style={{ color: cyan }}>location.</span>
            </h2>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1.1fr 1fr",
              maxWidth: "960px",
              margin: "0 auto",
              background: "rgba(87,215,226,0.03)",
              border: "1px solid rgba(87,215,226,0.12)",
              borderRadius: "24px",
              overflow: "hidden",
            }}
          >
            {/* Image side — Dubai skyline */}
            <div style={{ position: "relative", minHeight: isMobile ? "240px" : "360px" }}>
              <img
                src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1200&q=80&auto=format&fit=crop"
                alt="Dubai skyline — Indexed's operational headquarters"
                loading="lazy"
                decoding="async"
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: isMobile
                    ? "linear-gradient(to top, rgba(12,12,12,0.9), rgba(12,12,12,0.05))"
                    : "linear-gradient(to right, rgba(12,12,12,0) 45%, rgba(12,12,12,0.95))",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: "20px",
                  left: "20px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "rgba(8,8,8,0.55)",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  border: "1px solid rgba(255,255,255,0.14)",
                  borderRadius: "100px",
                  padding: "8px 16px",
                }}
              >
                <span style={{ fontSize: "18px" }}>🇦🇪</span>
                <span style={{ color: "#fff", fontSize: "14px", fontWeight: 700, fontFamily: ff }}>Dubai, UAE</span>
              </div>
            </div>

            {/* Content side */}
            <div
              style={{
                padding: isMobile ? "32px 24px" : "48px 44px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "18px" }}>
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "14px",
                    background: "rgba(87,215,226,0.08)",
                    border: "1px solid rgba(87,215,226,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg width="22" height="22" viewBox="0 0 18 18" fill="none">
                    <path d="M16 7.95455C16 13.3636 9 18 9 18C9 18 2 13.3636 2 7.95455C2 6.11009 2.7375 4.34117 4.05025 3.03694C5.36301 1.73271 7.14348 1 9 1C10.8565 1 12.637 1.73271 13.9497 3.03694C15.2625 4.34117 16 6.11009 16 7.95455Z" stroke={cyan} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9.5 10C10.8807 10 12 8.88071 12 7.5C12 6.11929 10.8807 5 9.5 5C8.11929 5 7 6.11929 7 7.5C7 8.88071 8.11929 10 9.5 10Z" stroke={cyan} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span style={{ color: "white", fontSize: isMobile ? "22px" : "26px", fontWeight: 700, fontFamily: ff }}>Dubai HQ</span>
              </div>
              <p style={{ color: cyan, fontSize: "15px", fontWeight: 600, fontFamily: ff, lineHeight: 1.6, margin: "0 0 12px" }}>
                Meydan Grandstand, Meydan Road, Nad Al Sheba
              </p>
              <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "15px", fontFamily: ff, lineHeight: 1.7, margin: "0 0 26px" }}>
                Our Dubai office serves as the operational headquarters, supporting clients across the Middle East and global markets.
              </p>
              <a
                href="https://share.google/AvRKwCYP0CI2KeICu"
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.background = "rgba(87,215,226,0.16)";
                  (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(87,215,226,0.4)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.background = "rgba(87,215,226,0.08)";
                  (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(87,215,226,0.2)";
                }}
                style={{
                  alignSelf: "flex-start",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "rgba(87,215,226,0.08)",
                  border: "1px solid rgba(87,215,226,0.2)",
                  color: cyan,
                  fontSize: "14px",
                  fontWeight: 700,
                  fontFamily: ff,
                  textDecoration: "none",
                  padding: "12px 22px",
                  borderRadius: "100px",
                  transition: "all 0.2s ease",
                }}
              >
                View on Google Maps →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          7. CTA — dramatic with larger glow
      ═══════════════════════════════════════════════════════════════ */}
      <section
        style={{
          background: "#080808",
          padding: isMobile ? "80px 20px 100px" : "140px 72px 160px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "700px", height: "400px", background: "radial-gradient(ellipse, rgba(87,215,226,0.08) 0%, transparent 60%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "0", left: "50%", transform: "translateX(-50%)", width: "500px", height: "200px", background: "radial-gradient(ellipse, rgba(87,215,226,0.05) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: "700px", margin: "0 auto", textAlign: "center" }}>
          <div style={{ width: "60px", height: "3px", background: `linear-gradient(90deg, transparent, ${cyan}, transparent)`, borderRadius: "2px", margin: "0 auto 40px" }} />
          <h2
            style={{
              color: "white",
              fontSize: isMobile ? "30px" : "clamp(36px, 4vw, 52px)",
              fontWeight: 700,
              fontFamily: ff,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              margin: "0 0 28px",
            }}
          >
            Let's <span style={{ color: cyan }}>talk.</span>
          </h2>
          <p
            style={{
              color: "rgba(255,255,255,0.55)",
              fontSize: isMobile ? "16px" : "19px",
              fontFamily: ff,
              lineHeight: 1.75,
              margin: "0 0 48px",
            }}
          >
            If your business has product-market fit, an established sales process, and organic search
            that should be generating more pipeline than it currently does — we should have a
            conversation.
          </p>
          <a
            href={VIDEO_REVIEW_URL}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
              border: "1px solid #57d7e2",
              borderRadius: "88px",
              padding: "18px 40px",
              boxShadow: "0px 4px 25px 0px rgba(87,215,226,0.25)",
              color: "#121212",
              fontSize: "16px",
              fontWeight: 600,
              fontFamily: ff,
              textDecoration: "none",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.opacity = "0.85";
              (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-2px)";
              (e.currentTarget as HTMLAnchorElement).style.boxShadow = "0px 6px 35px 0px rgba(87,215,226,0.35)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.opacity = "1";
              (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)";
              (e.currentTarget as HTMLAnchorElement).style.boxShadow = "0px 4px 25px 0px rgba(87,215,226,0.25)";
            }}
          >
            Get my free video review →
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>
      </section>
    </SiteLayout>
  );
}
